import type { GameSettings, GameState } from '@/types/game';

const STORAGE_KEYS = {
  GAME_STATE: 'gameState',
  SETTINGS: 'settings',
  GRAPHICS: 'graphics',
  EFFECTS: 'effects',
  SOUND: 'sound',
  VIBRATION: 'vibration',
  THEME: 'theme',
  TOTAL_SCORE: 'totalScore',
  ACHIEVEMENTS: 'achievements',
  GAME_MODE: 'gameMode',
  COMPLETED_LEVELS: 'completedLevels',
  LEVEL_STATS: 'levelStats',
  LAST_PLAYED_LEVEL: 'lastPlayedLevel',
  GAME_COMPLETION_MODE: 'gameCompletionMode',
} as const;

export class StorageService {
  static saveGameState(state: Partial<GameState>): void {
    try {
      const currentState = this.loadGameState();
      const newState = { ...currentState, ...state };
      localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(newState));
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  }

  static loadGameState(): Partial<GameState> | null {
    try {
      const savedState = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Failed to load game state:', error);
    }
    return null;
  }

  static getNextLevel(): number {
    const completedLevels = this.getCompletedLevels();
    
    // If no levels completed, start with tutorial (level 1)
    if (completedLevels.length === 0) {
      return 1;
    }
    
    // Find the highest completed level
    const maxCompletedLevel = Math.max(...completedLevels);
    
    // Next level is highest completed + 1 (converted to 1-based)
    // Level indices are 0-based, but display is 1-based
    return maxCompletedLevel + 2;
  }

  static hasSavedGame(): boolean {
    const savedState = this.loadGameState();
    return savedState !== null && savedState.level !== undefined && savedState.level > 0;
  }

  static clearGameState(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
    } catch (error) {
      console.error('Failed to clear game state:', error);
    }
  }

  static saveSettings(settings: Partial<GameSettings>): void {
    try {
      Object.entries(settings).forEach(([key, value]) => {
        const storageKey = key.toUpperCase() as keyof typeof STORAGE_KEYS;
        if (STORAGE_KEYS[storageKey]) {
          localStorage.setItem(STORAGE_KEYS[storageKey], String(value));
        }
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  static loadSettings(): GameSettings {
    return {
      graphics: (localStorage.getItem(STORAGE_KEYS.GRAPHICS) as GameSettings['graphics']) || 'high',
      effects: localStorage.getItem(STORAGE_KEYS.EFFECTS) !== 'false',
      sound: localStorage.getItem(STORAGE_KEYS.SOUND) !== 'false',
      vibration: localStorage.getItem(STORAGE_KEYS.VIBRATION) !== 'false',
      theme: (localStorage.getItem(STORAGE_KEYS.THEME) as GameSettings['theme']) || 'default',
    };
  }

  static getTotalScore(): number {
    return parseInt(localStorage.getItem(STORAGE_KEYS.TOTAL_SCORE) || '0');
  }

  static saveTotalScore(score: number): void {
    try {
      localStorage.setItem(STORAGE_KEYS.TOTAL_SCORE, String(score));
    } catch (error) {
      console.error('Failed to save total score:', error);
    }
  }

  static getAchievements(): string[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.ACHIEVEMENTS) || '[]');
    } catch (error) {
      console.error('Failed to load achievements:', error);
      return [];
    }
  }

  static saveAchievements(achievements: string[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.ACHIEVEMENTS, JSON.stringify(achievements));
    } catch (error) {
      console.error('Failed to save achievements:', error);
    }
  }

  static getGameMode(): GameState['gameMode'] {
    return (localStorage.getItem(STORAGE_KEYS.GAME_MODE) as GameState['gameMode']) || 'standard';
  }

  static saveGameMode(mode: GameState['gameMode']): void {
    try {
      localStorage.setItem(STORAGE_KEYS.GAME_MODE, mode);
    } catch (error) {
      console.error('Failed to save game mode:', error);
    }
  }

  static getCompletedLevels(): number[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.COMPLETED_LEVELS) || '[]');
    } catch (error) {
      console.error('Failed to load completed levels:', error);
      return [];
    }
  }

  static saveCompletedLevels(completedLevels: number[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.COMPLETED_LEVELS, JSON.stringify(completedLevels));
    } catch (error) {
      console.error('Failed to save completed levels:', error);
    }
  }

  static getLevelStats(): { [levelIndex: number]: import('@/types/game').LevelStats } {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.LEVEL_STATS) || '{}');
    } catch (error) {
      console.error('Failed to load level stats:', error);
      return {};
    }
  }

  static saveLevelStats(levelStats: { [levelIndex: number]: import('@/types/game').LevelStats }): void {
    try {
      localStorage.setItem(STORAGE_KEYS.LEVEL_STATS, JSON.stringify(levelStats));
    } catch (error) {
      console.error('Failed to save level stats:', error);
    }
  }

  static updateLevelStats(levelIndex: number, stats: Partial<import('@/types/game').LevelStats>): void {
    try {
      const currentStats = this.getLevelStats();
      const levelStats = currentStats[levelIndex] || {
        completed: false,
        bestTime: Infinity,
        bestScore: 0,
        totalAttempts: 0,
        stars: 0,
        firstCompletedAt: 0,
        lastPlayedAt: Date.now(),
      };

      const updatedStats = { ...levelStats, ...stats, lastPlayedAt: Date.now() };
      currentStats[levelIndex] = updatedStats;

      this.saveLevelStats(currentStats);
    } catch (error) {
      console.error('Failed to update level stats:', error);
    }
  }

  // Last played level tracking
  static saveLastPlayedLevel(levelIndex: number): void {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_PLAYED_LEVEL, String(levelIndex));
    } catch (error) {
      console.error('Failed to save last played level:', error);
    }
  }

  static getLastPlayedLevel(): number | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.LAST_PLAYED_LEVEL);
      return saved ? parseInt(saved, 10) : null;
    } catch (error) {
      console.error('Failed to load last played level:', error);
      return null;
    }
  }

  // Game completion mode tracking
  static setGameCompletionMode(isCompleted: boolean): void {
    try {
      localStorage.setItem(STORAGE_KEYS.GAME_COMPLETION_MODE, String(isCompleted));
    } catch (error) {
      console.error('Failed to save game completion mode:', error);
    }
  }

  static isInGameCompletionMode(): boolean {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.GAME_COMPLETION_MODE);
      return saved === 'true';
    } catch (error) {
      console.error('Failed to load game completion mode:', error);
      return false;
    }
  }

  // Get the appropriate continue level
  static getContinueLevel(): number {
    const completedLevels = this.getCompletedLevels();
    const lastPlayed = this.getLastPlayedLevel();
    const isCompletionMode = this.isInGameCompletionMode();
    
    // If in completion mode and has last played level, continue from there
    if (isCompletionMode && lastPlayed !== null) {
      return lastPlayed;
    }
    
    // Otherwise, calculate based on completed levels
    if (completedLevels.length === 0) {
      return 0; // Start with tutorial
    }
    
    // Find the highest completed level
    const maxCompletedLevel = Math.max(...completedLevels);
    
    // If all levels completed, use last played or start from beginning
    if (maxCompletedLevel >= 49) { // Assuming 50 levels (0-49)
      return lastPlayed !== null ? lastPlayed : 0;
    }
    
    // Otherwise, next uncompleted level
    return maxCompletedLevel + 1;
  }
}
